import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDeleteChain } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { ChainDefinitionSummary } from '@/types'
import { Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface DeleteChainDialogProps {
  projectId: string
  chain: ChainDefinitionSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteChainDialog({ projectId, chain, open, onOpenChange }: DeleteChainDialogProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const deleteChain = useDeleteChain(projectId)

  const isConfirmationValid = chain ? confirmationText === chain.displayName : false

  useEffect(() => {
    if (!open) {
      setConfirmationText('')
    }
  }, [open])

  const handleDelete = async () => {
    if (!isConfirmationValid || !chain) return

    try {
      await deleteChain.mutateAsync(chain.id)
      toast.success('Chain deleted successfully')
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? 'Failed to delete chain.')
    }
  }

  if (!chain) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete Chain</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the chain "<span className="font-bold">{chain.displayName}</span>"?
        </DialogDescription>
        <div className="space-y-2">
          <Input
            placeholder="Enter chain display name"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="w-full"
            aria-label="Type chain name to confirm deletion"
            data-testid="delete-chain-confirm-input"
          />
          <p className="text-sm text-muted-foreground">
            Type "{chain.displayName}" to enable the Delete button.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmationValid || deleteChain.isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-6 w-6" />
            {deleteChain.isPending ? 'Deleting...' : 'Delete Chain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
